import { DesktopRangeCalendar, MobileRangeCalendar } from '@src/components';
import { AccountType } from '@src/contexts';
import { useIsMobile } from '@src/hooks';
import { DashboardParams } from '@src/services/dashboard/dashboardTypes';
import { useGetSubAccountListQuery } from '@src/services/subAccount/subAccountList/subAccountListApiSlice';
import { useTheme } from '@src/theme';
import { Checkbox, Select, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

type FilterProps = {
    accountType: AccountType;
    setFilter: React.Dispatch<React.SetStateAction<DashboardParams>>;
    onAccountsLoaded?: (ids: string[]) => void;
};
export const Filter = ({
    accountType,
    onAccountsLoaded,
    setFilter,
}: FilterProps) => {
    const { theme } = useTheme();
    const isMobile = useIsMobile();

    const [open, setOpen] = useState(false);
    const { data: dataSubAccounts, isLoading } = useGetSubAccountListQuery({
        limit: 100,
        includeCurrent: false,
    });
    const accountOptions = useMemo(() => {
        const options =
            dataSubAccounts?.data?.items?.map((item) => ({
                label: item.name,
                value: item.id,
            })) || [];

        return options;
    }, [dataSubAccounts]);

    const rangeDateHandler = (range: [Dayjs, Dayjs] | [Date, Date] | null) => {
        setOpen(false);

        if (!range || !range[0] || !range[1]) {
            setFilter((prev) => {
                const { fromDate, toDate, ...rest } = prev;
                return rest;
            });
            return;
        }

        const fromDate = dayjs(range[0]).format('YYYY-MM-DD');
        const toDate = dayjs(range[1]).format('YYYY-MM-DD');

        setFilter((prev) => ({
            ...prev,
            fromDate,
            toDate,
        }));
    };

    const includeSubAccountsHandler = (e) => {
        setFilter((prev) => ({
            ...prev,
            includeSubAccounts: e.target.checked,
        }));
    };

    useEffect(() => {
        if (
            accountType !== 'myAccount' &&
            accountOptions.length > 0 &&
            onAccountsLoaded
        ) {
            onAccountsLoaded(accountOptions.map((opt) => opt.value));
        }
    }, [accountOptions, onAccountsLoaded]);

    const accountsHandler = (selectedIds: string[]) => {
        setFilter((prev) => ({
            ...prev,
            subAccountIds: selectedIds,
            subAccountName:
                selectedIds.length === 1
                    ? dataSubAccounts?.data.items.find(
                          (item) => item.id === selectedIds[0]
                      )?.name
                    : '',
        }));
    };

    const cancelClickedRef = useRef(false);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            const button = target.closest('.ant-btn') as HTMLElement | null;

            if (button && button.textContent?.trim() === 'Cancel') {
                cancelClickedRef.current = true;
            }
        };

        document.addEventListener('mousedown', handler, true);

        return () => {
            document.removeEventListener('mousedown', handler, true);
        };
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen) {
            if (cancelClickedRef.current) {
                cancelClickedRef.current = false;
                setOpen(false);
            } else {
                setOpen(true);
            }
        } else {
            setOpen(true);
        }
    };

    return (
        <div className="flex justify-between flex-wrap gap-2">
            <span
                className="mt-2 text-M-20"
                style={{ color: theme.semantic.textPrimary }}
            >
                {accountType === 'myAccount' ? 'My Account' : 'Sub-Accounts'}
            </span>
            <div className="flex items-start flex-wrap gap-2">
                {accountType === 'myAccount' ? (
                    <Checkbox
                        className="mt-1 font-normal"
                        value={true}
                        onChange={includeSubAccountsHandler}
                    >
                        <span
                            className="text-R-16"
                            style={{ color: theme.semantic.onBackground }}
                        >
                            Include Sub-Accounts
                        </span>
                    </Checkbox>
                ) : (
                    <Select
                        options={accountOptions}
                        mode="multiple"
                        placeholder="Accounts"
                        className="min-w-[150px] h-8 [&_.ant-select-selection-wrap]:!mt-0"
                        onChange={accountsHandler}
                        loading={isLoading}
                        disabled={isLoading}
                        showSearch
                        maxTagCount={1}
                        maxTagPlaceholder={(omittedValues) => (
                            <Tooltip
                                title={omittedValues.map((item) => (
                                    <div key={item.key}>{item.label}</div>
                                ))}
                            >
                                <span>+{omittedValues.length} more</span>
                            </Tooltip>
                        )}
                        defaultValue={
                            accountOptions.length > 0
                                ? accountOptions.map((option) => option.value)
                                : undefined
                        }
                    />
                )}
                {isMobile ? (
                    <MobileRangeCalendar
                        {...({
                            defaultValue: [null, null],
                            onChange: rangeDateHandler,
                            className: 'w-[294px]',
                            open: open,
                            onOpenChange: handleOpenChange,
                        } as any)}
                        disabledDate={(current) =>
                            current && current > dayjs().endOf('day')
                        }
                        placeholder="Select date range"
                    />
                ) : (
                    <DesktopRangeCalendar
                        {...({
                            defaultValue: [null, null],
                            onChange: rangeDateHandler,
                            className: 'w-[294px]',
                            open: open,
                            onOpenChange: handleOpenChange,
                        } as any)}
                        disabledDate={(current) =>
                            current && current > dayjs().endOf('day')
                        }
                    />
                )}
            </div>
        </div>
    );
};
